import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SpecialBankDto, SpecialCommandResultDto, SpecialSupDto } from "../../dto/Deal/SpecialFinanceDto";
import { SpecialPaymentCommandRequestDto } from "../../api/apiServices/backOffice/controllers/specialPayments/specialPayments.requestsDto";
import {
  SpecialBankMetadataRequestDto,
  SpecialBalanceCorrectionRequestDto,
  SpecialHistoryRequestDto,
} from "../../api/apiServices/auth/controllers/specialSettlementAccounts/specialSettlementAccounts.requestsDto";
import { FinanceContext, PaymentRow, InvoiceRow, HistoryRow, PaymentForm, InvoiceForm } from "./model";

export type PendingPayment = { command: SpecialPaymentCommandRequestDto; supId?: number };
type Data = {
  banks: SpecialBankDto[];
  payments: PaymentRow[];
  invoices: InvoiceRow[];
  sup: SpecialSupDto[];
  bankNames?: Record<number, string>;
};
const initialState: Data & {
  context: FinanceContext;
  loading: boolean;
  saving: boolean;
  busy: boolean;
  error?: string;
  pending?: PendingPayment;
  result?: SpecialCommandResultDto;
  history: HistoryRow[];
  historyTotal: number;
  historyLoading: boolean;
  historyPage: number;
  historyAccountId?: number;
  revision: number;
} = {
  context: {},
  banks: [],
  payments: [],
  invoices: [],
  sup: [],
  loading: false,
  saving: false,
  busy: false,
  history: [],
  historyTotal: 0,
  historyLoading: false,
  historyPage: 1,
  revision: 0,
};
const slice = createSlice({
  name: "specialFinance",
  initialState,
  reducers: {
    load(state, action: PayloadAction<FinanceContext>) {
      state.context = action.payload;
      state.banks = [];
      state.payments = [];
      state.invoices = [];
      state.sup = [];
      state.history = [];
      state.historyAccountId = undefined;
      state.bankNames = undefined;
      if (action.payload.readOnly) state.pending = undefined;
      state.loading = true;
      state.error = undefined;
    },
    loadSuccess(state, action: PayloadAction<Data>) {
      Object.assign(state, action.payload);
      state.loading = false;
    },
    loadReject(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    execute(state, _action: PayloadAction<PaymentForm>) {
      state.busy = true;
      state.error = undefined;
    },
    executeSuccess(state, action: PayloadAction<SpecialCommandResultDto>) {
      state.result = action.payload;
      state.busy = false;
      state.pending = undefined;
      state.revision++;
    },
    executeReject(state, action: PayloadAction<string>) {
      state.busy = false;
      state.error = action.payload;
    },
    remember(state, action: PayloadAction<PendingPayment>) {
      state.pending = action.payload;
    },
    forgetPending(state) {
      state.pending = undefined;
    },
    retry(state) {
      state.busy = true;
      state.error = undefined;
    },
    retrySuccess(state) {
      state.busy = false;
    },
    retryReject(state, action: PayloadAction<string>) {
      state.busy = false;
      state.error = action.payload;
    },
    saveBank(state, _action: PayloadAction<SpecialBankMetadataRequestDto>) {
      state.saving = true;
      state.error = undefined;
    },
    saveBankSuccess(state) {
      state.saving = false;
      state.revision++;
    },
    saveBankReject(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },
    correctBank(state, _action: PayloadAction<{ id: number; dto: SpecialBalanceCorrectionRequestDto }>) {
      state.saving = true;
      state.error = undefined;
    },
    correctBankSuccess(state) {
      state.saving = false;
      state.revision++;
    },
    correctBankReject(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },
    closeBank(state, _action: PayloadAction<number>) {
      state.saving = true;
      state.error = undefined;
    },
    closeBankSuccess(state) {
      state.saving = false;
      state.revision++;
    },
    closeBankReject(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },
    saveInvoice(state, _action: PayloadAction<InvoiceForm>) {
      state.saving = true;
      state.error = undefined;
    },
    saveInvoiceSuccess(state) {
      state.saving = false;
      state.revision++;
    },
    saveInvoiceReject(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },
    history(state, action: PayloadAction<SpecialHistoryRequestDto>) {
      state.historyLoading = true;
      state.historyAccountId = action.payload.id;
      state.historyPage = action.payload.page;
    },
    historySuccess(state, action: PayloadAction<{ rows: HistoryRow[]; total: number }>) {
      state.historyLoading = false;
      state.history = action.payload.rows;
      state.historyTotal = action.payload.total;
    },
    historyReject(state, action: PayloadAction<string>) {
      state.historyLoading = false;
      state.error = action.payload;
    },
    closeHistory(state) {
      state.historyAccountId = undefined;
      state.history = [];
    },
  },
});
export default slice;
